import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import styled from 'styled-components';

import Checkbox from 'components/common/checkbox';
import connect from 'components/lib/connect';
import InlineContainer from 'components/common/containers/inline_container';
import ResetUserActivationToken from 'actions/user/reset_user_activation_token';
import { Roles } from 'models/role';
import Tooltip from 'components/common/tooltip';
import { TR, TD } from 'components/common/table';
import User from 'models/user';
import { TeamsContext } from 'components/admin/users/teams_provider';

const AgentNameWrapper = styled(InlineContainer).attrs({ inset: 'none', inline: 'small' })`
  flex-direction: row;
  align-items: center;
`;

export const AgentName = styled.div`
  font-weight: bold;
`;

export const UserRow = styled(TR)`
  &.editableUser:hover {
    color: ${p => p.theme.colors.green500};
    cursor: pointer;
  }
`;

const CheckboxCell = styled(TD)`
  width: 36px;
  text-align: right;
`;

const WrappedCell = styled(TD)`
  word-break: break-word;
`;

const InviteStatusCell = styled(TD)`
  min-width: 100px;
`;

const UserInviteStatusText = styled.span`
  &.inviteExpired {
    color: ${p => p.theme.colors.red500};
  }
`;

export function UsersTableRow({
  agent,
  canSelectUsers,
  canEditUsers,
  isSelected,
  isSsoEnabled,
  onUserSelectClick,
  onResendInviteClick,
  routingGroups,
  user,
}) {
  const teams = useContext(TeamsContext);

  if (!user) {
    return null;
  }

  let filteredTeams = _.filter(teams, t => t.agentIds.has(user.id));
  return (
    <UserRow
      className={classnames({ editableUser: canEditUsers || canSelectUsers })}
      onClick={handleUserClick}
      data-aid="userRow"
    >
      {canSelectUsers ? (
        <CheckboxCell>
          <Checkbox id={user.id} checked={isSelected} />
        </CheckboxCell>
      ) : null}
      <WrappedCell canSelectUsers={canSelectUsers}>
        <AgentNameWrapper>
          <div className="profileAvatar-placeholderIcon" />
          {renderUserName({ name: agent.name, email: agent.email })}
        </AgentNameWrapper>
      </WrappedCell>
      <WrappedCell>{renderElementsOfType(filteredTeams, 'teams')}</WrappedCell>
      <WrappedCell>
        {renderElementsOfType(
          _.map(user.roleIds, id => Roles[id]),
          'roles'
        )}
      </WrappedCell>
      {canSelectUsers ? null : <TD>{renderElementsOfType(routingGroups, 'inboxes')}</TD>}
      {canSelectUsers ? null : <InviteStatusCell>{renderUserStatus()}</InviteStatusCell>}
    </UserRow>
  );

  function handleUserClick() {
    (canSelectUsers || canEditUsers) && onUserSelectClick && onUserSelectClick(user.id);
  }

  function renderUserName({ name, email }) {
    return (
      <div>
        <AgentName>{name || 'User'}</AgentName>
        <div data-aid="usersTableRow-email">{email}</div>
      </div>
    );
  }

  function renderUserStatus() {
    if (user.isDisabled()) {
      return <div data-aid="disabledStatus">Disabled</div>;
    }

    if (!isSsoEnabled) {
      return <InviteStatus user={user} canEditUsers={canEditUsers} onResendInviteClick={onResendInviteClick} />;
    }

    return null;
  }
}

UsersTableRow.propTypes = {
  agent: PropTypes.shape({
    email: PropTypes.string,
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  canEditUsers: PropTypes.bool,
  canSelectUsers: PropTypes.bool,
  isSelected: PropTypes.bool,
  isSsoEnabled: PropTypes.bool,
  onUserSelectClick: PropTypes.func.isRequired,
  onResendInviteClick: PropTypes.func.isRequired,
  routingGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  teams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  user: PropTypes.instanceOf(User),
};

function renderElementsOfType(collection, type) {
  let capitalizedType = _.capitalize(type);
  if (collection.length === 0) {
    return <span data-aid={`noUser${capitalizedType}`}>{`0 ${type}`}</span>;
  }

  if (collection.length === 1) {
    return <span data-aid={`singleUser${capitalizedType}`}>{collection[0].name}</span>;
  }

  const maxElementsDisplayedInTooltip = 5;
  let displayItems = _.take(collection, maxElementsDisplayedInTooltip).map(item => item.name);
  if (collection.length > maxElementsDisplayedInTooltip) {
    displayItems.push(`+ ${collection.length - 5} more`);
  }
  let tooltipMessage = displayItems.join('\r\n');
  return (
    <Tooltip className={`multipleUser${capitalizedType}Tooltip`} delay={250} message={tooltipMessage} position="top">
      <span data-aid={`multipleUser${capitalizedType}`}>
        {collection[0].name} + {collection.length - 1} more
      </span>
    </Tooltip>
  );
}

export function InviteStatus({ canEditUsers, onResendInviteClick, user }) {
  if (user.isActivated() || user.isDisabled()) {
    return null;
  }

  return (
    <UserInviteStatus
      canEditUsers={canEditUsers}
      hasExpired={user.hasInviteExpired()}
      onResendInviteClick={onResendInviteClick}
    />
  );
}

InviteStatus.propTypes = {
  canEditUsers: PropTypes.bool,
  user: PropTypes.instanceOf(User),
  onResendInviteClick: PropTypes.func.isRequired,
};

export class UserInviteStatus extends React.Component {
  constructor(props) {
    super(props);
    _.bindAll(this, ['handleMouseHover', 'handleResendInvite']);
    this.state = {
      isHovering: false,
    };
  }

  handleMouseHover() {
    if (this.props.canEditUsers) {
      this.setState({ isHovering: !this.state.isHovering });
    }
  }

  render() {
    return (
      <UserInviteStatusText
        className={classnames({ inviteExpired: this.props.hasExpired && !this.state.isHovering })}
        data-aid="inviteStatus"
        onMouseEnter={this.handleMouseHover}
        onMouseLeave={this.handleMouseHover}
        onClick={this.handleResendInvite}
      >
        {this.state.isHovering ? 'Resend invite' : this.props.hasExpired ? 'Invite expired' : 'Invite sent'}
      </UserInviteStatusText>
    );
  }

  handleResendInvite(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    this.props.canEditUsers && this.props.onResendInviteClick();
  }
}

UserInviteStatus.propTypes = {
  canEditUsers: PropTypes.bool,
  hasExpired: PropTypes.bool,
  onResendInviteClick: PropTypes.func.isRequired,
};

const UsersTableRowContainer = connect(mapStateToProps, mapExecuteToProps)(UsersTableRow);

function mapStateToProps({ getProvider, isFeatureEnabled }, { agent }) {
  const user = getProvider('users').findBy({ id: agent.id });
  if (!user) {
    return {};
  }

  const routingGroups = getProvider('routingGroups').findAll({
    select: ['id', 'name'],
    filter: routingGroup => routingGroup.agentIds.indexOf(agent.id) !== -1,
  });

  return {
    canEditUsers: isFeatureEnabled('userManagement'),
    isSsoEnabled: isFeatureEnabled('sso'),
    routingGroups,
    user,
  };
}

function mapExecuteToProps(executeAction, { agent }) {
  return {
    onResendInviteClick: () => executeAction(ResetUserActivationToken, agent.id),
  };
}

UsersTableRowContainer.propTypes = {
  agent: PropTypes.shape({
    email: PropTypes.string,
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  canSelectUsers: PropTypes.bool,
  isSelected: PropTypes.bool,
  onUserSelectClick: PropTypes.func,
};

export default UsersTableRowContainer;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/users/users_table_row.jsx